import { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import './AddKPI.scss';
import {
  Button,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { validationSchema, initialValues } from './constant';
import addPrimaryIcon from '../../../../assets/images/performance/addPrimaryIcon.svg';
import addKpi from '../../../../assets/images/performance/addKpi.png';
import selectKpi from '../../../../assets/images/performance/selectKpi.png';
import {
  createPerformance,
  createPerformanceHeader,
} from '../../../../store/apis/performanceService';

function AddKPI({
  isOpen,
  toggle,
  KPIs,
  selectedKPI,
  selectedEmployee,
  performanceDetails,
  refetchPerformanceDetails,
  performanceHeader,
  refetchPerformanceHeader,
}) {
  const headerData = performanceHeader?.[0];

  const [showKPIModal, setKPIModal] = useState(false);
  const [addedKPINames, setAddedKPINames] = useState([]);

  const { mutate: createHeader } = useMutation({
    mutationFn: createPerformanceHeader,
    onSuccess: () => {
      refetchPerformanceHeader();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to fetch data', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (performanceDetails) {
      const names = performanceDetails.map(detail => detail.KPI_Name);
      setAddedKPINames(names);
    }
  }, [performanceDetails]);

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset();
    if (selectedKPI?.KPI_Category) {
      setValue('name', selectedKPI.KPI_Name);
      setValue('description', selectedKPI.KPI_Details);
    }
  }, [reset, selectedKPI, setValue]);

  const { mutate: addKPI, isPending } = useMutation({
    mutationFn: createPerformance,
    onSuccess: () => {
      toast.success(
        selectedKPI?.KPI_Category
          ? 'KPI updated successfully!'
          : 'KPI added successfully!',
        {
          className: 'toast-success',
        },
      );
      refetchPerformanceDetails();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to add kpi', {
        className: 'toast-error',
      });
    },
  });

  const getRandomSeq = () => {
    const usedSeqs =
      performanceDetails?.map(d => d?.KPI_Sequence).filter(Boolean) || [];
    let seq;
    do {
      seq = Math.floor(Math.random() * 100) + 1; // 1 to 10000
    } while (usedSeqs.includes(seq));
    return seq;
  };

  const onSubmit = data => {
    const headerPayload = {
      employeeId: selectedEmployee?.value,
      department: headerData?.Department?.trim(),
      designation: headerData?.Designation?.trim(),
      clasification: headerData?.Clasification?.trim(),
      doj: headerData?.DOJ?.trim(),
      mgr: headerData?.Mgr?.trim(),
      deptMgr: headerData?.DeptMgr?.trim(),
      userId: '',
    };
    createHeader(headerPayload);

    const kpiSeq = getRandomSeq();
    const payload = {
      kpiCategory:
        selectedKPI?.KPI_Category ??
        `${selectedEmployee?.value}-${new Date().getFullYear()}-${kpiSeq}`,
      kpiSeq: selectedKPI?.KPI_Sequence ?? kpiSeq,
      kpiName: data.name,
      kpiDetails: data.description,
      employeeId: selectedEmployee?.value,
      userId: '',
    };
    addKPI(payload);
    toggle();
    reset();
  };

  const toggleKPI = event => {
    event.stopPropagation();
    setKPIModal(!showKPIModal);
  };

  const handleSelectKPI = async kpi => {
    setValue('name', kpi.KPI_Name, { shouldDirty: true, shouldValidate: true });
    setValue('description', kpi.KPI_Description, {
      shouldDirty: true,
      shouldValidate: true,
    });
    await trigger(['name', 'description']); // Trigger validation for both fields
    setKPIModal(false);
  };

  return (
    <div>
      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end">
        <OffcanvasHeader>
          <div className="addKpiHeader">
            <img alt="" src={addKpi} />
            <div className="title">
              {selectedKPI?.KPI_Category ? 'View KPI' : 'Add New KPI'}
            </div>
          </div>
        </OffcanvasHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <OffcanvasBody>
            <div className="addKpiBody">
              <div className="kpiInput">
                <div className="kpiName">
                  <div className="input">
                    <Label>
                      Enter KPI Name
                      <span className="text-danger"> *</span>
                    </Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="text"
                          placeholder="Enter"
                          className="external-filter-input form-control"
                          {...field}
                          title="Qualitative job-related KPIs. All employees must use SMART objectives. KPIs need to be linked to business line of sight."
                        />
                      )}
                    />
                    {errors.name && (
                      <span className="text-danger">{errors.name.message}</span>
                    )}
                  </div>

                  <div className="divider">
                    <span>OR</span>
                  </div>
                  <Button
                    className="btn-primary-light btn-active"
                    onClick={toggleKPI}
                    type="button"
                    title="Select from the predefined objectives list"
                  >
                    <img src={addPrimaryIcon} alt="" height={20} />
                    Select from Library
                  </Button>
                </div>
                <div className="kpiDescription">
                  <Label>
                    Description
                    <span className="text-danger"> *</span>
                  </Label>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <ReactQuill
                        value={field.value}
                        onChange={field.onChange}
                        title="Enter the KPI description. Should include details and criterias of the KPI."
                        placeholder="Enter"
                      />
                    )}
                  />
                  {errors.description && (
                    <span className="text-danger">
                      {errors.description.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="kpiAction">
                <Button
                  className="custom-light-btn item"
                  onClick={() => {
                    reset();
                    toggle(); // this closes the Offcanvas from parent
                  }}
                  type="button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="custom-btn item"
                  style={{
                    backgroundColor: '#017EBD',
                    border: '#017EBD',
                  }}
                  disabled={
                    isPending ||
                    !isValid ||
                    (selectedKPI?.KPI_Category && !isDirty)
                  }
                >
                  {(() => {
                    if (isPending) {
                      return selectedKPI?.KPI_Category
                        ? 'Updating...'
                        : 'Saving...';
                    }
                    return selectedKPI?.KPI_Category ? 'Update' : 'Save';
                  })()}
                </Button>
              </div>
            </div>
          </OffcanvasBody>
        </form>
      </Offcanvas>
      <Modal isOpen={showKPIModal} toggle={toggleKPI} centered>
        <ModalHeader toggle={toggleKPI}>
          <div className="addKpiHeader">
            <img alt="Select KPI" src={addKpi} />
            <div className="title">Select KPI</div>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="modalKpiBody">
            {KPIs?.result?.map((kpi, index) => {
              if (addedKPINames.includes(kpi.KPI_Name)) {
                return null; // Skip KPIs that are already added
              }
              return (
                <Button
                  key={index}
                  className="select-kpi-button item"
                  onClick={() => handleSelectKPI(kpi)}
                >
                  <img src={selectKpi} alt="" height={14} />
                  {kpi.KPI_Name}
                </Button>
              );
            })}
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default AddKPI;
